import { DATA_BASE, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../core/config';
import type { Language, Locale } from '../core/types';
import { qs } from '../core/utils';
import { applySeo } from './seo';

let currentLocale: Locale | null = null;
let currentLanguage: Language = DEFAULT_LANGUAGE;

const getLanguagePath = (language: Language): string => {
  if (language === 'en') {
    return '/';
  }
  return `/${language}/`;
};

const loadLocale = async (language: Language): Promise<Locale> => {
  const response = await fetch(`${DATA_BASE}/locales/${language}.json`);
  if (!response.ok) {
    throw new Error(`Failed to load locale ${language}`);
  }
  return response.json() as Promise<Locale>;
};

export const getCurrentLocale = (): Locale | null => currentLocale;

export const getCurrentLanguage = (): Language => currentLanguage;

export const translate = (key: string): string | undefined => {
  if (!currentLocale) return undefined;
  const parts = key.split('.');
  let value: unknown = currentLocale;
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof value === 'string' ? value : undefined;
};

export const applyTranslations = (): void => {
  const elements = document.querySelectorAll('[data-i18n]');
  elements.forEach((element) => {
    const key = element.getAttribute('data-i18n');
    if (!key) return;
    const translation = translate(key);
    if (translation) {
      element.textContent = translation;
    }
  });
};

export const setLanguage = async (language: Language): Promise<void> => {
  if (!SUPPORTED_LANGUAGES.includes(language)) {
    return;
  }
  currentLanguage = language;
  currentLocale = await loadLocale(language);
  applyTranslations();
  document.documentElement.lang = language;
  document.dispatchEvent(new CustomEvent('vajaja:language-change', { detail: { language } }));
  applySeo(language, currentLocale);
  window.history.replaceState({}, '', getLanguagePath(language));
  localStorage.setItem('vajaja-lang', language);

  const buttons = document.querySelectorAll('.lang-switcher__btn');
  buttons.forEach((button) => {
    button.classList.toggle('is-active', button.getAttribute('data-lang') === language);
  });
};

export const initLanguage = (): void => {
  const pageLanguage = document.documentElement.lang as Language | null;
  const savedLanguage = localStorage.getItem('vajaja-lang') as Language | null;
  const initialLanguage = pageLanguage && SUPPORTED_LANGUAGES.includes(pageLanguage)
    ? pageLanguage
    : savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)
      ? savedLanguage
      : DEFAULT_LANGUAGE;

  const switcher = qs('.lang-switcher');
  switcher?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const lang = target.getAttribute('data-lang') as Language | null;
    if (lang) {
      setLanguage(lang);
    }
  });

  setLanguage(initialLanguage);
};
